import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useApi, errMsg, fmtIST } from "@/lib/useApi";
import { Sel, Field } from "./QuestionEditor";

const BLANK = { family: "UGC_NET", cycle: "", title: "", exam_start: "", exam_end: "", source_url: "", verification_status: "unverified", notes: "" };
const local = (s) => (s ? s.slice(0, 16) : "");

export default function ExamDates() {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/admin/announcements").then((r) => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [api]);

  const save = async () => {
    const body = { ...form, exam_end: form.exam_end || null };
    try {
      form.id ? await api.put(`/admin/announcements/${form.id}`, body) : await api.post("/admin/announcements", body);
      toast.success("Saved"); setForm(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/announcements/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-exam-dates-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">Official exam dates</h1>
          <p className="text-slate-600 mt-2 text-sm">Students see a countdown only for <b>verified</b> future announcements with an official source. Times are India Standard Time.</p>
        </div>
        <Button onClick={() => setForm(BLANK)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ann-new-btn">Add announcement</Button>
      </div>
      {!rows.length && <Card className="p-8 border-slate-200 text-center text-slate-600">No announcements recorded. Students currently see “Official exam date not announced yet.”</Card>}
      {rows.map((a) => (
        <Card key={a.id} className="p-5 border-slate-200 flex flex-wrap items-center justify-between gap-3" data-testid={`ann-row-${a.id}`}>
          <div>
            <p className="font-medium">{a.family.replace("_", " ")} · {a.cycle} — {a.title}</p>
            <p className="text-xs text-slate-500 mt-1">{fmtIST(a.exam_start)} → {fmtIST(a.exam_end)} · {a.source_url ? <a className="text-blue-600 underline" href={a.source_url} target="_blank" rel="noreferrer">source</a> : "no source"}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={a.verification_status === "verified" ? "border-emerald-200 text-emerald-700" : "border-amber-200 text-amber-700"}>{a.verification_status}</Badge>
            <Button size="sm" variant="outline" onClick={() => setForm({ ...a, exam_start: local(a.exam_start), exam_end: local(a.exam_end) })}>Edit</Button>
            <Button size="sm" variant="outline" className="text-red-700" onClick={() => del(a.id)}>Delete</Button>
          </div>
        </Card>
      ))}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="bg-white" data-testid="ann-dialog">
          <DialogHeader><DialogTitle>{form?.id ? "Edit" : "New"} announcement</DialogTitle></DialogHeader>
          {form && <div className="grid sm:grid-cols-2 gap-3">
            <Field l="Family"><Sel value={form.family} onChange={(v) => setForm({ ...form, family: v })} options={[["UGC_NET", "UGC NET"], ["CSIR_NET", "CSIR NET"]]} testid="ann-family" /></Field>
            <Field l="Cycle (e.g. December 2026)"><Input value={form.cycle} onChange={(e) => setForm({ ...form, cycle: e.target.value })} data-testid="ann-cycle" /></Field>
            <Field l="Title" className="sm:col-span-2"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ann-title" /></Field>
            <Field l="Start (IST)"><Input type="datetime-local" value={form.exam_start} onChange={(e) => setForm({ ...form, exam_start: e.target.value })} data-testid="ann-start" /></Field>
            <Field l="End (IST, optional)"><Input type="datetime-local" value={form.exam_end || ""} onChange={(e) => setForm({ ...form, exam_end: e.target.value })} data-testid="ann-end" /></Field>
            <Field l="Official source URL" className="sm:col-span-2"><Input value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} data-testid="ann-source" /></Field>
            <Field l="Verification"><Sel value={form.verification_status} onChange={(v) => setForm({ ...form, verification_status: v })} options={[["unverified", "Unverified (hidden from students)"], ["verified", "Verified official announcement"]]} testid="ann-status" /></Field>
            <Field l="Notes"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          </div>}
          <DialogFooter><Button onClick={save} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ann-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
